import {
  ShaderMaterial, VertexBuffer, type AbstractMesh, type CascadedShadowGenerator, type RenderTargetTexture, type Scene,
} from "@babylonjs/core";

// World-space slack keeps tangent surfaces and Float32 projection boundaries.
const CULL_MARGIN = 0.001;
type RenderListCallback = NonNullable<RenderTargetTexture["getCustomRenderList"]>;

/**
 * Conservative caster selection in each cascade's actual, texel-snapped light
 * projection. Call bind() after construction, map resizing/recreation and context
 * restoration; dispose() before disposing the generator. The authoritative
 * observed renderList is never replaced or mutated.
 *
 * Babylon 9.30 computes cascade matrices in RTT onBeforeBind, then establishes
 * the layer in onBeforeRender, then calls getCustomRenderList(layer, ...). Use
 * that callback, not a camera-frustum observer or a shared scene transform.
 */
export class CascadeShadowCulling {
  private target: RenderTargetTexture | null = null;
  private previous: RenderTargetTexture["getCustomRenderList"] = null;
  private readonly lists: AbstractMesh[][] = [];

  constructor(private readonly scene: Scene, private readonly shadow: CascadedShadowGenerator) {}

  bind(): void {
    const target = this.shadow.getShadowMap();
    if (target === this.target && target?.getCustomRenderList === this.select) return;
    this.detach();
    if (!target) return;
    // One plain list per layer; only lifecycle changes allocate lists.
    while (this.lists.length < this.shadow.numCascades) this.lists.push([]);
    this.target = target;
    this.previous = target.getCustomRenderList;
    target.getCustomRenderList = this.select;
  }

  dispose(): void {
    this.detach();
    for (let i = 0; i < this.lists.length; i++) this.lists[i]!.length = 0;
  }

  private detach(): void {
    if (this.target?.getCustomRenderList === this.select) this.target.getCustomRenderList = this.previous;
    this.target = null;
    this.previous = null;
  }

  private readonly select: RenderListCallback = (layer, renderList, renderListLength) => {
    const prior = this.previous?.(layer, renderList, renderListLength) ?? null;
    const source = prior ?? renderList;
    const sourceLength = prior ? prior.length : renderListLength;
    const list = this.lists[layer];
    const matrix = this.shadow.getCascadeTransformMatrix(layer);
    // Unknown projections/custom vertex shaders must keep upstream behavior.
    if (!source || !list || !matrix || this.scene.floatingOriginMode
      || typeof this.scene.customLODSelector === "function" || this.shadow.customShaderOptions) return prior;
    const m = matrix.m;
    const xx = m[0]!, xy = m[4]!, xz = m[8]!, xd = m[12]!;
    const yx = m[1]!, yy = m[5]!, yz = m[9]!, yd = m[13]!;
    const w = m[15]!;
    const xScale = Math.hypot(xx, xy, xz), yScale = Math.hypot(yx, yy, yz);
    const margin = CULL_MARGIN + Math.abs(this.shadow.normalBias);
    if (m[3] !== 0 || m[7] !== 0 || m[11] !== 0 || !(w > 0) || !(xScale > 0) || !(yScale > 0)
      || !Number.isFinite(xScale + yScale + xd + yd + w + margin)) return prior;
    // Normal bias displaces normalized world normals by at most |normalBias|.
    // Project that world-space sphere onto each XY axis, independently of Z.
    const xPad = margin * xScale, yPad = margin * yScale;
    let count = 0;
    for (let i = 0; i < sourceLength; i++) {
      const mesh = source[i];
      if (!mesh || mesh.isDisposed()) continue;
      // Force only stale transforms, including ancestors changed in this render
      // id. Frozen static scenery retains Babylon's frozen-world semantics.
      mesh.computeWorldMatrix(!mesh.isSynchronized());
      // Cached base bounds cannot prove exclusion for these rendering paths.
      if (mesh.infiniteDistance || mesh.alwaysSelectAsActiveMesh || mesh.billboardMode !== 0
        || mesh.skeleton || mesh.morphTargetManager || mesh.bakedVertexAnimationManager?.isEnabled
        || mesh.doNotSyncBoundingInfo || !mesh.hasBoundingInfo
        || mesh.getVertexBuffer(VertexBuffer.PositionKind)?.isUpdatable()
        || mesh.hasThinInstances || mesh.isAnInstance || mesh.material instanceof ShaderMaterial
        || mesh.material?.getClassName() === "NodeMaterial"
        || ("instances" in mesh && (mesh.instances as AbstractMesh[]).length > 0)
        || ("getLODLevels" in mesh && (mesh as AbstractMesh & { getLODLevels(): unknown[] }).getLODLevels().length > 0)) {
        list[count++] = mesh;
        continue;
      }
      const bounds = mesh.getBoundingInfo();
      if (!bounds || bounds.isLocked) {
        list[count++] = mesh;
        continue;
      }
      const { minimumWorld: min, maximumWorld: max } = bounds.boundingBox;
      if (!Number.isFinite(min.x + min.y + min.z + max.x + max.y + max.z)
        || min.x > max.x || min.y > max.y || min.z > max.z) {
        list[count++] = mesh;
        continue;
      }
      // Exact interval of a world AABB along each light-projection axis. Never
      // test Z: depth-clamped upstream/off-camera occluders can still cast here.
      const minX = xx * (xx >= 0 ? min.x : max.x) + xy * (xy >= 0 ? min.y : max.y)
        + xz * (xz >= 0 ? min.z : max.z) + xd;
      const maxX = xx * (xx >= 0 ? max.x : min.x) + xy * (xy >= 0 ? max.y : min.y)
        + xz * (xz >= 0 ? max.z : min.z) + xd;
      if (maxX < -w - xPad || minX > w + xPad) continue;
      const minY = yx * (yx >= 0 ? min.x : max.x) + yy * (yy >= 0 ? min.y : max.y)
        + yz * (yz >= 0 ? min.z : max.z) + yd;
      const maxY = yx * (yx >= 0 ? max.x : min.x) + yy * (yy >= 0 ? max.y : min.y)
        + yz * (yz >= 0 ? max.z : min.z) + yd;
      if (maxY < -w - yPad || minY > w + yPad) continue;
      list[count++] = mesh;
    }
    list.length = count;
    return list;
  };
}
